// The page-wide River player (one per tab), created after the first server time fetch.
import { useEffect, useState, useSyncExternalStore } from "react";
import { applyPatch } from "@eb/shared";
import { onLive, setListening, startLive } from "../live";
import { fetchServerTime, RiverPlayer } from "./player";
import { ServerSource } from "./serverSource";
import { FakeSource } from "./source";

let playerPromise: Promise<RiverPlayer> | null = null;

export function riverPlayer(): Promise<RiverPlayer> {
  playerPromise ??= (async () => {
    const { epochMs } = await fetchServerTime();
    // `?source=fake` plays the dev fake day (no server lineups needed).
    const fake = new URLSearchParams(location.search).get("source") === "fake";
    const p = new RiverPlayer(fake ? new FakeSource(epochMs) : new ServerSource(epochMs));
    await p.syncClock();
    startLive();
    onLive((m) => {
      if (m.type === "hello") void p.refreshLineups();
      if (m.type === "patch") {
        void p
          .lineup(m.day)
          .then((l) =>
            p.setLineup(
              applyPatch(l, {
                bpm: l.bpm,
                day: m.day,
                version: m.version,
                entry: m.entry,
                entries: m.entries,
                reason: m.reason,
              }),
            ),
          )
          .catch(() => undefined);
      }
    });
    p.onChange(() => setListening(p.status === "playing"));
    if (typeof window !== "undefined") (window as unknown as { __ebRiver?: RiverPlayer }).__ebRiver = p;
    return p;
  })();
  playerPromise.catch(() => (playerPromise = null));
  return playerPromise;
}

/** The player once ready (null while loading), re-rendering on every player change. */
export function useRiverPlayer(): RiverPlayer | null {
  const [player, setPlayer] = useState<RiverPlayer | null>(null);
  useEffect(() => {
    let alive = true;
    void riverPlayer().then((p) => alive && setPlayer(p));
    return () => {
      alive = false;
    };
  }, []);
  const version = useSyncExternalStore(
    (cb) => (player ? player.onChange(cb) : () => undefined),
    () =>
      player
        ? `${player.status}:${player.nudges}:${player.offsetMs}:${player.lineupGeneration}:${player.previewing}`
        : "",
  );
  void version;
  return player;
}

/** A clock that re-renders every `ms` milliseconds. */
export function useTicker(ms: number): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((x) => x + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return n;
}

/** The player if it exists already (never creates one). */
export async function existingRiverPlayer(): Promise<RiverPlayer | null> {
  return playerPromise ? playerPromise.catch(() => null) : null;
}

/** End a song preview if one is playing (leaving a page, the stop button). */
export async function stopPreview(): Promise<void> {
  const p = await existingRiverPlayer();
  if (p?.previewing) await p.endPreview();
}

/** Stop the River if it is playing (e.g. when the editor opens, so it doesn't play over your
 *  song). Doesn't create the player if the River was never opened. */
export async function stopRiverIfPlaying(): Promise<void> {
  if (!playerPromise) return;
  const p = await playerPromise.catch(() => null);
  if (p && (p.status === "playing" || p.status === "joining")) p.stop();
}
